import { HttpStatusCode } from '@app/types';

const adminContact = 'If the problem continues, contact your workspace admin.';

/** Pull a backend-supplied message out of the response body, if present. */
async function readDetail(response: Response): Promise<string> {
  let text = '';
  try {
    text = await response.text();
    const json: unknown = JSON.parse(text);
    if (
      json
      && typeof json === 'object'
      && 'message' in json
      && typeof json.message === 'string'
    ) {
      return json.message;
    }
  } catch {
    // Intentionally left blank.
  }

  return (!text) ? '' : text;
}

/** Turn an MFA HTTP failure into an explanation of the cause & how to retry. */
async function responseToMessage(response: Response): Promise<string[]> {
  const details = (await readDetail(response)).replace(/\.$/, '');
  const possibleDetails = (!details) ? '' : ` (${details})`;

  switch (response.status) {
    case HttpStatusCode.badRequest:
    case HttpStatusCode.unauthorized:
    case HttpStatusCode.unprocessableEntity:
      return [
        `The verification code was incorrect or has expired${possibleDetails}.`,
        'Open your authenticator app and enter the current six-digit code, then try again.',
        adminContact
      ];
    case HttpStatusCode.forbidden:
      return [
        `Your account isn't permitted to complete multi-factor authentication${possibleDetails}.`,
        'Contact your workspace admin to confirm your access.'
      ];
    case HttpStatusCode.notFound:
      return [
        `Multi-factor authentication isn't set up for this account${possibleDetails}.`,
        'Contact your workspace admin for help enabling it.'
      ];
    case HttpStatusCode.locked:
    case HttpStatusCode.tooManyRequests:
      return [
        `Too many failed attempts — your account is temporarily locked${possibleDetails}.`,
        'Wait a few minutes before trying again.',
        adminContact
      ];
    default:
      break;
  }

  if (response.status >= HttpStatusCode.internalServerError) {
    return [
      `The authentication service is temporarily unavailable${possibleDetails}.`,
      'This is a server-side issue — please retry in a few minutes.',
      adminContact
    ];
  }

  return [
    `Multi-factor authentication failed (${response.status} ${details ?? response.statusText})`,
    adminContact
  ];
}

/**
 * Build a human-readable explanation for an error thrown during the MFA login
 * flow. HTTP failures are thrown as raw {@link Response} objects, which the
 * login forms cannot otherwise describe, so this reads the status & body to
 * explain the cause, who to contact, and how to retry.
 */
export async function mfaErrorMessage(error: unknown): Promise<string> {
  if (error instanceof Response) {
    return (await responseToMessage(error)).join('\n');
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unknown error occurred.';
}
